import { SupabaseClient } from '@supabase/supabase-js';
import { CalendarDate } from '../../shared/types/triple.mts';
import { TUTORIAL_ID } from './Constants.tsx';
import { KeyValueStore } from './KeyValueStore';
import {
    ANONYMOUS_USER_ID,
    FieldValue,
    GameWideData,
    LocalGameWideRecord,
    LocalGameWideRecordParams,
    LocalRecordParams,
    practiceTriplit,
    PuzzleKey,
    PuzzleState,
    PuzzleStateParams,
    PuzzleStateRecord,
    RawLegacyStateNames,
    rawLegacyStateNames,
    SafeFieldRegistry,
    TimeElapsedKey,
    RawLegacyState,
} from './State.tsx';
import { applyTimeout, isGameWideStateKey, isLegacyGlobalStateKey, isPuzzleKey, isTimeElapsedKey } from './utils.ts';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Mutex } from 'async-mutex';
import { useLoginSession } from './SessionContext.tsx';
import { useInactivityContext } from './InactivityContext.tsx';
import * as Sentry from '@sentry/capacitor';

type UserId = string | null;
type PuzzleId = string;

type UserState = {
    puzzlesById: Map<PuzzleId, PuzzleStateRecord>;
    puzzlesByDate: Map<string, PuzzleStateRecord>;
    gameWideRecord: LocalGameWideRecord;
    timeElapsedById: Map<PuzzleId, number>; // Legacy support for time elapsed
    lastPushHadConflict: boolean;
};

type SyncPullItemsResult = {
    items: PullResponseItem[];
    next_after_seq: number;
    has_more: boolean;
};

type SyncPushItemsResults = {
    results: PushResponseItem[];
    ok: number;
    conflict: number;
};

type PushResponseItem = {
    item_key: string;
    ok: boolean;
    new_version: number;
    sync_seq: number;
    updated_at: number;
};

type PullResponseItem = {
    user_id: string;
    item_key: string;
    item_value: Record<string, any> | null;
    deltas_applied: string[] | null;
    row_version: number;
    sync_seq: number;
    updated_at: string;
};

type PushRequestItem = {
    user_id: string;
    item_key: string;
    new_item_value: Record<string, any> | null;
    deltas_applied: string[] | null;
    base_version: number;
};

const ITEM_PUSH_LIMIT = 40;

export enum SyncResult {
    DONE,
    MORE_TO_SYNC,
    NO_USER,
    ERROR,
}

export enum SyncStatus {
    IDLE,
    SYNCING,
    ERROR,
}

export class PuzzleIndex {
    private store: KeyValueStore;
    private userMap: Map<UserId, UserState> = new Map();
    private supabaseClient: SupabaseClient<any, 'public', 'public', any, any>;
    private syncMutex = new Mutex();
    lastSyncFailed = false;

    constructor(store: KeyValueStore, supabaseClient: SupabaseClient<any, 'public', 'public', any, any>) {
        this.store = store;
        this.supabaseClient = supabaseClient;
    }

    static emptyUserState(userId: string): UserState {
        return {
            puzzlesById: new Map(),
            puzzlesByDate: new Map(),
            timeElapsedById: new Map(),
            gameWideRecord: LocalGameWideRecord.emptyUserState(userId),
            lastPushHadConflict: false,
        };
    }

    async init(): Promise<void> {
        await this.migrateToQualifiedPuzzleKeys();
        const allKeys = (await this.store.getAllKeys()).sort();
        const puzzleKeys = allKeys.filter(Boolean).filter(isPuzzleKey);
        const timeElapsedKeys = allKeys.filter(Boolean).filter(isTimeElapsedKey);
        const legacyGlobalStateKeys = allKeys.filter(Boolean).filter(isLegacyGlobalStateKey);
        const gameWideStateKey = allKeys.filter(Boolean).filter(isGameWideStateKey);
        const puzzles = await this.store.getItems(puzzleKeys);
        const timeElapsedItems = await this.store.getItems(timeElapsedKeys);
        const gameWideStates = await this.store.getItems(gameWideStateKey);
        this.populateIndex(puzzleKeys, puzzles, timeElapsedItems, gameWideStates);
        const migratedGlobalState = (await this.store.getValue('migrated_to_syncable_state')) === 'true';
        if (!migratedGlobalState) {
            const legacyGlobalStateMap = (await this.store.getItems(legacyGlobalStateKeys)) as Map<
                RawLegacyStateNames,
                string
            >;
            const legacyGlobalState: RawLegacyState = Object.fromEntries(
                rawLegacyStateNames.map((key) => [key, legacyGlobalStateMap.get(key) ?? null])
            ) as RawLegacyState;
            console.info('Migrating legacy global state to per-user state');
            const record = LocalGameWideRecord.fromLegacyFormat(legacyGlobalState);
            this.getOrInitUserState(ANONYMOUS_USER_ID);
            const userData = this.userMap.get(ANONYMOUS_USER_ID);
            if (userData == null) {
                throw new Error('User data should have been initialized');
            }
            userData.gameWideRecord = record;
            await record.saveLocally(this.store);
            await this.store.setValue('migrated_to_syncable_state', 'true');
        } else {
            console.info('Legacy global state has already been migrated');
        }
        // const start = Date.now();
        // await this.trySyncData(STARTUP_SYNC_TIMEOUT_MS);
        // console.info(`Initial sync took ${Date.now() - start}ms`);
    }

    async trySyncData(timeoutMs: number = 10000): Promise<SyncResult> {
        // Serialize syncs
        return this.syncMutex.runExclusive(async () => {
            try {
                return await applyTimeout(this.syncData(), timeoutMs);
            } catch (e) {
                console.warn('Error syncing data:', e);
                return SyncResult.ERROR;
            }
        });
    }

    private getDirtyRecords(userId: string): (LocalGameWideRecord | PuzzleStateRecord)[] {
        const dirtyRecords: (LocalGameWideRecord | PuzzleStateRecord)[] = [];
        const userState = this.userMap.get(userId);
        if (userState) {
            // Gather all dirty items
            // Prioritize game-wide record
            if (userState.gameWideRecord.dirty) {
                dirtyRecords.push(userState.gameWideRecord);
            }
            for (const [id, record] of userState.puzzlesById) {
                if (record.dirty) {
                    dirtyRecords.push(record);
                }
            }
        }
        return dirtyRecords;
    }

    private async syncData(): Promise<SyncResult> {
        // Get the currently signed in user
        const supabaseSession = await this.supabaseClient.auth.getSession();
        const user = supabaseSession.data.session?.user;
        if (!user) {
            console.info('No user signed in, cannot sync data');
            const session = await this.supabaseClient.auth.getSession();
            if (session.data.session) {
                // User appears to have been signed in, but doesn't have a valid token.
                // Set the lastSyncFailed flag to prompt the user to sign in again.
                this.lastSyncFailed = true;
            }
            return SyncResult.NO_USER;
        }
        // Step 1: Pull
        const userId = user.id;
        const lastSyncSeqKey = `lastSyncSeq_${userId}`;
        const afterSeq = await this.store.getValue(lastSyncSeqKey);
        const limit = 400;
        const params = new URLSearchParams({
            after: afterSeq ?? '0',
            limit: String(limit),
        });
        const pullResult = await this.supabaseClient.functions.invoke<SyncPullItemsResult>(
            `sync_pull_items?${params.toString()}`,
            {
                headers: { 'X-Triplit-Protocol': '1' },
            }
        );
        if (pullResult.error) {
            throw new Error(`Error pulling sync items: ${pullResult.error.message}`);
        }
        if (pullResult.data?.items.length === 0) {
            if (this.getOrInitUserState(userId).lastPushHadConflict) {
                // This is a signal that the app may be open in multiple tabs, and another
                // tab advanced the after parameter beyond what this tab has read.
                console.warn(
                    'No new items to pull, but last sync had conflict. Maybe multiple tabs are open? Clearing lastSyncSeq.'
                );
                this.getOrInitUserState(userId).lastPushHadConflict = false;
                await this.store.removeItem(lastSyncSeqKey);
                return SyncResult.MORE_TO_SYNC;
            }
        }
        const moreToPull = pullResult.data?.has_more ?? false;
        const syncPullItemsResult = pullResult.data;
        if (!syncPullItemsResult) {
            throw new Error('No data returned from sync pull');
        }
        console.info(`Pulled ${syncPullItemsResult.items.length} items from server`);

        // Step 2: Merge
        for (const item of syncPullItemsResult.items) {
            if (item.item_value == null) {
                continue;
            }
            try {
                if (item.user_id != userId) {
                    console.warn(`Got item for different user: ${item.user_id}`);
                    continue;
                }
                // Process the item...
                const recordParams: LocalRecordParams<any> = {
                    data: item.item_value,
                    userId: userId,
                    dirty: false, // Pulled directly from server, so by definition not dirty
                    deltasApplied: item.deltas_applied ?? [],
                    lastModifiedMs: new Date(item.updated_at).getDate(),
                    lastSyncedVersion: item.row_version,
                };
                let record: LocalGameWideRecord | PuzzleStateRecord;
                if (item.item_key === LocalGameWideRecord.getRemoteKey()) {
                    // This is a game-wide state item
                    record = new LocalGameWideRecord(recordParams as LocalGameWideRecordParams);
                } else {
                    // This is a puzzle state item
                    record = new PuzzleStateRecord(recordParams as LocalRecordParams<PuzzleStateParams>);
                }
                await this.mergeAndUpdateIndex(record);
            } catch (e) {
                console.error(`Error processing sync item ${item.item_key}:`, e);
                Sentry.captureException(e);
            }
        }

        // Step 3: Push
        // Gather all dirty items
        const moreToPush = this.getDirtyRecords(userId).length > ITEM_PUSH_LIMIT;
        const recordsToPush = this.getDirtyRecords(userId).slice(0, ITEM_PUSH_LIMIT);
        recordsToPush.forEach((r) => r.markCleanSincePush());
        const speculativeCopies = recordsToPush.map((record: LocalGameWideRecord | PuzzleStateRecord) => {
            // Speculatively apply all deltas to a copy. Merging prunes old deltas but does not apply new ones.
            const copy = record.deepCopy();
            copy.applyAllDeltas();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return copy;
        });

        if (speculativeCopies.length > 0) {
            const requestItems = speculativeCopies.map((record) => {
                const serialized = record.serialize();
                const requestItem: PushRequestItem = {
                    user_id: serialized.userId,
                    item_key: record.getRemoteKey(),
                    new_item_value: serialized.data as Record<string, any>,
                    // We do not push deltas back to the server
                    deltas_applied: serialized.deltasApplied ?? [],
                    base_version: serialized.lastSyncedVersion ?? 0,
                };
                return requestItem;
            });

            const pushResults = await this.supabaseClient.functions.invoke<SyncPushItemsResults>(`sync_push_items`, {
                method: 'POST',
                headers: { 'X-Triplit-Protocol': '1' },
                body: JSON.stringify({ items: requestItems }),
            });

            if (pushResults.error) {
                throw new Error(`Error pushing sync items: ${pushResults.error.message}`);
            }
            const pushItemsResults = pushResults.data;
            if (!pushItemsResults) {
                throw new Error('No data returned from sync push');
            }
            const okResults = pushItemsResults.results.filter((r) => r.ok);
            console.info(`Pushed ${pushItemsResults.results.length} items to server. ${okResults.length} succeeded.`);
            const userState = this.getOrInitUserState(userId);
            userState.lastPushHadConflict = pushItemsResults.conflict > 0;

            for (const pushItemResult of pushItemsResults.results) {
                const record: LocalGameWideRecord | PuzzleStateRecord | undefined = this.findCorrespondingRecord(
                    pushItemResult,
                    recordsToPush
                );
                if (!record) {
                    console.warn(`Could not find local record for pushed item ${pushItemResult.item_key}`);
                    continue;
                }

                const speculativeCopy = this.findCorrespondingRecord(pushItemResult, speculativeCopies);
                if (!speculativeCopy) {
                    console.warn(`Could not find local record for pushed item ${pushItemResult.item_key}`);
                    continue;
                }

                if (pushItemResult.ok) {
                    // The speculative copy is now the latest version of the record.
                    speculativeCopy.markClean();
                    // Update the version so we don't do a needless merge on the next pull.
                    speculativeCopy.lastSyncedVersion = pushItemResult.new_version;
                    await this.mergeAndUpdateIndex(speculativeCopy, /* successfullyPushed= */ true);
                } else {
                    // We merged locally, but the server rejected the change.
                    // Ensure the local record continues to be considered dirty.
                    record.markDirty();
                    await record.saveLocally(this.store);
                }
            }
        } else {
            console.info('No local changes to push');
        }

        // Step 4: Update last sync sequence
        await this.store.setValue(lastSyncSeqKey, syncPullItemsResult.next_after_seq.toString());

        // For now, consider a round of syncing to be done after conflicts. They are not unexpected, and it may be
        // better to back off rather than immediately trying to sync again the way we would if we were just very behind.
        return moreToPull || moreToPush ? SyncResult.MORE_TO_SYNC : SyncResult.DONE;
    }

    private findCorrespondingRecord = (
        pushItemResult: PushResponseItem,
        records: (LocalGameWideRecord | PuzzleStateRecord)[]
    ): LocalGameWideRecord | PuzzleStateRecord | undefined => {
        return records.find((r) => {
            if (pushItemResult.item_key === LocalGameWideRecord.getRemoteKey()) {
                // Return the one game-wide record
                return r instanceof LocalGameWideRecord;
            } else {
                return (
                    r instanceof PuzzleStateRecord && r.data.triple.id === PuzzleKey.parse(pushItemResult.item_key)?.id
                );
            }
        });
    };

    private async mergeAndUpdateIndex(
        record: LocalGameWideRecord | PuzzleStateRecord,
        successfullyPushed: boolean = false
    ): Promise<void> {
        const userState = this.getOrInitUserState(record.userId);
        let merged: typeof record;
        let current: typeof record | undefined;
        if (record instanceof PuzzleStateRecord) {
            const puzzleId = record.data.triple.id;
            current = userState.puzzlesById.get(puzzleId);
            merged = record.merge(current);
            userState.puzzlesById.set(puzzleId, merged);
            if (record.data.triple.date) {
                const dateKey = record.data.triple.date.toString();
                userState.puzzlesByDate.set(dateKey, merged);
            }
        } else {
            current = userState.gameWideRecord;
            merged = record.merge(current);
            userState.gameWideRecord = merged;
        }
        if (successfullyPushed && !record.isDirty() && current && !current.dirtySincePush) {
            // If we haven't modified the record since pushing, and the merge didn't change anything,
            // consider the merged record to be clean to avoid endlessly pushing the same data.
            merged.markClean();
        }
        await merged.saveLocally(this.store);
    }

    private async migrateToQualifiedPuzzleKeys() {
        const flagName = 'migratedToQualifiedPuzzleKeys';
        const alreadyMigrated = (await this.store.getValue(flagName)) === 'true';
        if (alreadyMigrated) {
            console.info('Puzzle state has already been migrated');
            return;
        }
        console.info('Migrating puzzle state to qualified keys');
        const puzzleKeys = (await this.store.getAllKeys()).filter(isPuzzleKey);
        const legacyPuzzleKeys = puzzleKeys.filter((key) => key.split('_').length === 2);
        const legacyTimeElapsedKeys = legacyPuzzleKeys.map((key) => {
            const parts = key.split('_');
            return new TimeElapsedKey(parts[1]).toString();
        });
        const legacyPuzzles = await this.store.getItems(legacyPuzzleKeys);
        const timeElapsedItems = await this.store.getItems(legacyTimeElapsedKeys);
        for (const [legacyPuzzleKey, value] of legacyPuzzles) {
            try {
                const obj = JSON.parse(value) as PuzzleState;
                if (typeof obj !== 'object' || obj === null) {
                    console.warn(`Skipping migration of corrupted puzzle state for key ${legacyPuzzleKey}`);
                    return;
                }
                const parts = legacyPuzzleKey.split('_');
                // triplit_<puzzleId>
                const puzzleId = parts[1];
                const timeElapsedKey = new TimeElapsedKey(puzzleId).toString();
                const timeElapsedVal = timeElapsedItems.get(timeElapsedKey);
                let timeElapsed: number | undefined;
                try {
                    timeElapsed = timeElapsedVal ? (JSON.parse(timeElapsedVal) as number) : undefined;
                } catch (e) {
                    console.warn('Time elapsed value seems corrupted for puzzle ' + puzzleId + ', resetting to 0');
                    timeElapsed = 0;
                }
                const combinedData = new PuzzleState({ ...obj, timeElapsed });
                const newRecord = new PuzzleStateRecord({ userId: ANONYMOUS_USER_ID, data: combinedData });
                await newRecord.saveLocally(this.store);
                await this.store.removeItem(legacyPuzzleKey);
                await this.store.removeItem(timeElapsedKey);
            } catch (e) {
                console.warn(`Skipping migration of corrupted puzzle state for key ${legacyPuzzleKey}: ${e}`);
            }
        }
        await this.store.setValue(flagName, 'true');
    }

    private populateIndex(
        puzzleKeys: string[],
        items: Map<string, string>,
        timeElapsedItems: Map<string, string>,
        gameWideStates: Map<string, string>
    ) {
        puzzleKeys
            .map((key): PuzzleStateRecord | null => {
                try {
                    // For legacy support, merge in all the timeElapsed values, which used to be stored in separate key-value.
                    // pairs.
                    const str = items.get(key);
                    if (str == null) {
                        throw new Error('No data found for key ' + key);
                    }
                    // TODO: cast as narrower params object once implemented for PuzzleState
                    const value = JSON.parse(str) as object;
                    // For legacy purposes, find the correpsonding timeElapsed value, if any. It used to
                    // be stored separately.
                    const parts = key.split('_');
                    if (parts.length < 2 || parts.length > 3) {
                        throw new Error('Unexpected puzzle key format: ' + key);
                    }
                    // triplit_<puzzleId>[_<userId>]
                    const puzzleId = parts[1];
                    const userId = parts.length === 3 ? parts[2] : undefined;
                    let record: PuzzleStateRecord;
                    if (userId == null) {
                        // Legacy key without user ID
                        const timeElapsedVal = timeElapsedItems.get(new TimeElapsedKey(puzzleId, userId).toString());
                        const timeElapsed = timeElapsedVal ? (JSON.parse(timeElapsedVal) as number) : undefined;
                        record = new PuzzleStateRecord({
                            userId: ANONYMOUS_USER_ID,
                            data: { ...(value as PuzzleStateParams), timeElapsed },
                        });
                    } else {
                        record = new PuzzleStateRecord(value as LocalRecordParams<PuzzleStateParams>);
                    }
                    return record;
                } catch (e) {
                    console.warn(`Puzzle state for key ${key} in key-value store seems corrupted: ${e}`);
                    return null;
                }
            })
            .filter((record) => record != null)
            .forEach((puzzleStateRecord) => {
                const key = puzzleStateRecord.userId ?? ANONYMOUS_USER_ID;
                const userState = this.getOrInitUserState(key);
                userState.puzzlesById.set(puzzleStateRecord.data.triple.id, puzzleStateRecord);
                if (puzzleStateRecord.data.triple.date) {
                    userState.puzzlesByDate.set(puzzleStateRecord.data.triple.date.toString(), puzzleStateRecord);
                }
            });

        gameWideStates.forEach((value, key) => {
            let mapKey: string;
            try {
                if (key.split('_').length === 2) {
                    mapKey = key.split('_')[1];
                } else if (key.split('_').length === 1) {
                    mapKey = ANONYMOUS_USER_ID;
                } else {
                    throw new Error('Unexpected key format: ' + key);
                }
                const userState = this.getOrInitUserState(mapKey);
                const record = new LocalGameWideRecord(JSON.parse(value) as LocalGameWideRecordParams);
                userState.gameWideRecord = record;
            } catch (e) {
                console.warn(`Game-wide state value for key ${key} in key-value store seems corrupted: ${e}`);
                return;
            }
        });
    }

    getGameWideDataSnapshot(userId?: string): GameWideData {
        return (
            this.userMap.get(userId ?? ANONYMOUS_USER_ID)?.gameWideRecord.getInitialSnapshot() ?? ({} as GameWideData)
        );
    }

    async setGameWideField<K extends keyof SafeFieldRegistry>(
        field: K,
        value: FieldValue<K>,
        userId?: string
    ): Promise<void> {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(mapKey);
        // In general it is not safe to simply overwrite a field, since it may clobber
        // changes made on another device. Therefore we treat this as a merge operation.
        const copy = userState.gameWideRecord.deepCopy();
        copy.setField(field, value);
        await this.mergeAndUpdateIndex(copy);
    }

    async updateStreak(victoryDate: Date, streakLength: number, userId?: string): Promise<void> {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(mapKey);
        const copy = userState.gameWideRecord.deepCopy();
        copy.updateStreakLocally(victoryDate, streakLength);
        await this.mergeAndUpdateIndex(copy);
    }

    async recordWin(turns: number, userId?: string): Promise<void> {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(mapKey);
        const copy = userState.gameWideRecord.deepCopy();
        copy.recordWin(turns);
        await this.mergeAndUpdateIndex(copy);
    }

    async recordLoss(userId?: string): Promise<void> {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(mapKey);
        const copy = userState.gameWideRecord.deepCopy();
        copy.recordLoss();
        await this.mergeAndUpdateIndex(copy);
    }

    async recordBonusPuzzleUnlock(userId?: string): Promise<void> {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(mapKey);
        const copy = userState.gameWideRecord.deepCopy();
        copy.recordBonusUnlock();
        await this.mergeAndUpdateIndex(copy);
    }

    getPuzzleStateById(id: string, userId?: string): PuzzleState | undefined {
        if (id === TUTORIAL_ID) {
            return new PuzzleState({
                triple: practiceTriplit(),
            });
        }
        userId = userId ?? ANONYMOUS_USER_ID;
        return this.userMap.get(userId)?.puzzlesById.get(id)?.data;
    }

    getPuzzleStateByIdOrThrow(id: string, userId?: string): PuzzleState {
        const res = this.getPuzzleStateById(id, userId);
        if (!res) {
            throw new Error(`No puzzle state found with id ${id} for user ${userId ?? ANONYMOUS_USER_ID}`);
        }
        return res;
    }

    getPuzzleStateByDate(date: CalendarDate, userId?: string): PuzzleState | undefined {
        userId = userId ?? ANONYMOUS_USER_ID;
        return this.userMap.get(userId)?.puzzlesByDate.get(date.toString())?.data;
    }

    async mergeAnonymousDataWithUserIfNeeded(userId: string): Promise<void> {
        const alreadyMergedKey = `mergedAnonymousDataWith_${userId}`;
        const alreadyMerged = (await this.store.getValue(alreadyMergedKey)) === 'true';
        if (alreadyMerged) {
            console.info(`Anonymous data has already been merged with user ${userId}`);
            return;
        }
        const anonymousData = this.userMap.get(ANONYMOUS_USER_ID);
        if (!anonymousData) return;
        const anonState = this.getOrInitUserState(ANONYMOUS_USER_ID);

        // Merge game-wide record
        const anonGameWideRecord = anonState.gameWideRecord.deepCopy();
        anonGameWideRecord.userId = userId;
        await this.mergeAndUpdateIndex(anonGameWideRecord);

        // Merge puzzle-level data
        anonymousData.puzzlesById.forEach(async (anonymousPuzzleRecord, puzzleId) => {
            if (puzzleId === TUTORIAL_ID) return; // Don't transfer the tutorial puzzle
            const copy = anonymousPuzzleRecord.deepCopy();
            copy.userId = userId;
            await this.mergeAndUpdateIndex(copy);
        });
        await this.store.setValue(alreadyMergedKey, 'true');

        // For now, do NOT delete the anonymous data in the user map
        // in case the Puzzle gets re-rendered with the anonymous user ID again.
        // This would break the invariant that Puzzle always has a PuzzleStateRecord for the active triplit id.
        // this.userMap.delete(ANONYMOUS_USER_ID);
    }

    async createOrUpdate(puzzleState: PuzzleState, userId?: string): Promise<void> {
        userId = userId ?? ANONYMOUS_USER_ID;
        const userState = this.getOrInitUserState(userId);
        const puzzleStateRecord =
            userState.puzzlesById.get(puzzleState.triple.id)?.copyWithNewData(puzzleState) ??
            PuzzleStateRecord.createNew(puzzleState, userId);
        if (puzzleState.triple.date) {
            const possiblyStaleRecord = userState.puzzlesByDate.get(puzzleState.triple.date.toString());
            if (possiblyStaleRecord && possiblyStaleRecord.data.triple.id !== puzzleState.triple.id) {
                // If there was a different puzzle recorded for this date, remove the date from
                // the other record and its puzzle data
                possiblyStaleRecord.data = new PuzzleState({
                    ...possiblyStaleRecord.data,
                    triple: possiblyStaleRecord.data.triple.copyAndClearDate(),
                });
                possiblyStaleRecord.markDirty();
                await possiblyStaleRecord.saveLocally(this.store);
            }
        }
        await this.mergeAndUpdateIndex(puzzleStateRecord);
    }

    private getOrInitUserState(userId?: string) {
        const mapKey = userId ?? ANONYMOUS_USER_ID;
        let val = this.userMap.get(mapKey);
        if (val == null) {
            val = PuzzleIndex.emptyUserState(mapKey);
            this.userMap.set(mapKey, val);
        }
        return val;
    }

    size(): number {
        let total = 0;
        this.userMap.forEach(({ puzzlesById }) => {
            total += puzzlesById.size;
        });
        return total;
    }
}

type PuzzleIndexContextAPI = {
    puzzleIndex: PuzzleIndex;
    syncVersion: number;
    syncStatus: SyncStatus;
    doSync: () => Promise<SyncResult>;
};
export const PuzzleIndexContext = createContext<PuzzleIndexContextAPI | undefined>(undefined);
export function usePuzzleIndex(): PuzzleIndexContextAPI {
    const ctx = useContext(PuzzleIndexContext);
    if (!ctx) {
        throw new Error('usePuzzleIndex must be used within a PuzzleIndexContext.Provider');
    }
    return ctx;
}

export function PuzzleIndexContextProvider({
    puzzleIndex,
    children,
}: {
    puzzleIndex: PuzzleIndex;
    children: ReactNode;
}) {
    const [syncVersion, setVersion] = useState<number>(0); // Incremented on each sync so consumers rerender
    const [syncStatus, setSyncStatus] = useState<SyncStatus>(SyncStatus.IDLE);
    const syncInterval = useRef<NodeJS.Timeout>();
    const { session } = useLoginSession();
    const { userIsActive } = useInactivityContext();
    const promiseRef = useRef<Promise<SyncResult> | null>(null);

    const doSync = useCallback(async () => {
        const result = await puzzleIndex.trySyncData();
        switch (result) {
            case SyncResult.DONE:
                setSyncStatus(SyncStatus.IDLE);
                break;
            case SyncResult.MORE_TO_SYNC:
                setTimeout(doSync, 0); // Immediately try again in a separate call stack
                setSyncStatus(SyncStatus.SYNCING);
                break;
            case SyncResult.NO_USER:
                if (session) {
                    // We have a session, but couldn't sync. This likely means the token is invalid.
                    setSyncStatus(SyncStatus.ERROR);
                } else {
                    setSyncStatus(SyncStatus.IDLE);
                }
                break;
            case SyncResult.ERROR:
                setSyncStatus(SyncStatus.ERROR);
                break;
        }
        setVersion((v) => v + 1);
        return result;
    }, [puzzleIndex, session]);

    const value: PuzzleIndexContextAPI = useMemo(() => {
        return {
            puzzleIndex,
            syncVersion,
            syncStatus,
            doSync,
        };
    }, [puzzleIndex, syncVersion, syncStatus, doSync]);

    useEffect(() => {
        syncInterval.current = setInterval(async () => {
            if (promiseRef.current) {
                // A sync is already in progress
                console.info('Scheduled sync already in progress, skipping this one');
                return;
            }
            if (!userIsActive) {
                // Don't sync while the user is inactive
                console.info('User is inactive, skipping scheduled sync');
                return;
            }
            promiseRef.current = doSync();
            try {
                await promiseRef.current;
            } finally {
                promiseRef.current = null;
            }
        }, 5000); // Every 5 seconds
        // Just on the initial mount and when the user session changes, show a syncing status
        // (if we did it on every sync, the UI would be constantly flickering)
        setSyncStatus(SyncStatus.SYNCING);
        void doSync(); // Initial sync on mount

        return () => {
            clearInterval(syncInterval.current);
        };
    }, [session, doSync, userIsActive]);

    return <PuzzleIndexContext.Provider value={value}>{children}</PuzzleIndexContext.Provider>;
}
